import { values } from "lodash";
import debounce from "lodash.debounce";
import React, { useCallback, useState, useEffect } from "react";
import {
  formatDuration,
  intervalToDuration,
  format,
  parse,
  isBefore,
  isAfter,
} from "date-fns";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBan,
  FaPlusCircle,
  FaAdjust,
} from "react-icons/fa";
import {
  FaMobileAlt,
  FaMoneyBillWave,
  FaCreditCard,
  FaUser,
} from "react-icons/fa";
// colors
export let Colors = {
  blue: "#0080FF",
  indigo: "#5b5efb",
  purple: "#8927f9",
  pink: "#f24a8b",
  red: "#df3c4e",
  orange: "#f85f32",
  yellow: "#f2bc16",
  green: "#2dc58c",
  teal: "#2acc9c",
  cyan: "#478ffc",
  black: "#000",
  white: "#fff",
  gray: "#6B7280",
  grayDark: "#374151",
  gray100: "#f1f2f7",
  gray200: "#E5E7EB",
  gray300: "#D1D5DB",
  gray400: "#6B7280",
  gray500: "#6B7280",
  gray600: "#4B5563",
  gray700: "#374151",
  gray800: "#1F2937",
  gray900: "#111827",
  primary: "#5f38f9",
  secondary: "#374151",
  success: "#2dc58c",
  info: "#478ffc",
  warning: "#f2bc16",
  danger: "#df3c4e",
  lighter: "#f1f2f7",
  light: "#E5E7EB",
  dark: "#1F2937",
  darker: "#111827",

  bodyColor: "#787c9e",
  bodyBg: "#f8f8f9",
  borderColor: "#d2d4e4",
  borderColorTranslucent: "rgba(0, 0, 0, 0.175)",

  linkColor: "#5f38f9",
  linkHoverColor: "#4c2dc7",
  codeColor: "#f24a8b",
  highlightBg: "#fcf2d0",
};

// list of all months in english word
export let monthList = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

let getToday = function () {
  let today = new Date();
  let dd = String(today.getDate()).padStart(2, "0");
  let mm = String(today.getMonth() + 1).padStart(2, "0");
  let yyyy = today.getFullYear();

  return yyyy + "-" + mm + "-" + dd;
};
export let today = getToday();

let getTesterday = function () {
  let today = new Date();
  let yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  let y_dd = String(yesterday.getDate()).padStart(2, "0");
  let y_mm = String(yesterday.getMonth() + 1).padStart(2, "0");
  let y_yyyy = yesterday.getFullYear();

  return y_yyyy + "-" + y_mm + "-" + y_dd;
};
export let yesterday = getTesterday();

let getCurrentMonth = function () {
  let today = new Date();
  let mm = String(today.getMonth() + 1).padStart(2, "0");
  let yyyy = today.getFullYear();

  return yyyy + "-" + mm;
};

export let currentMonth = getCurrentMonth();

// Get first and last character from string
let getInitials = function (name) {
  if (!name || typeof name !== "string") {
    return "";
  }

  const names = name.trim().split(" ");
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export let toInitials = getInitials;

export let Break = {
  mb: 420,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  any: Infinity,
};

export function formatUnderscoreString(inputString) {
  return inputString
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}

export const capitalizeWord = (word) =>
  word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : "";

export const dateRangeOptions = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7_days", label: "Last 7 Days" },
  { value: "15_days", label: "Last 15 Days" },
  { value: "30_days", label: "Last 30 Days" },
  { value: "last_month", label: "Last Month" },
  { value: "3_months", label: "Last 3 Months" },
  { value: "custom", label: "Custom Date" },
];

export const perPageOptions = [
  { value: 10, label: "10 Per Page" },
  { value: 25, label: "25 Per Page" },
  { value: 50, label: "50 Per Page" },
  { value: 100, label: "100 Per Page" },
];

export const reportOption = [
  { label: "Bill & Expense Report", value: "bill and expense report" },
];

export function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

export function formatDateTimeWithAdd5Hours(datetimeStr) {
  let date;

  // Try to parse "05 Aug 2025, 05:47 AM" format
  if (/[a-zA-Z]{3}/.test(datetimeStr)) {
    // Convert "05 Aug 2025, 05:47 AM" → Date object
    const parts = datetimeStr.split(", ");
    const datePart = parts[0]; // "05 Aug 2025"
    const timePart = parts[1]; // "05:47 AM"

    date = new Date(`${datePart} ${timePart}`);
  } else {
    // Convert "2025-08-05 05:47:00" → Date object
    date = new Date(datetimeStr.replace(" ", "T"));
  }

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  // Add 5 hours 30 minutes
  const offsetMillis = (5 * 60 + 30) * 60 * 1000;
  const adjustedDate = new Date(date.getTime() + offsetMillis);

  const options = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return adjustedDate.toLocaleString("en-US", options).replace(",", "");
}

export const getYearsOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];

  for (let year = currentYear; year > currentYear - 5; year--) {
    years.push({
      label: year.toString(),
      value: year,
    });
  }

  return years;
};

export function getMonthOptions(year) {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  let lastMonthIndex;

  if (year < currentYear) {
    lastMonthIndex = 11;
  } else if (year === currentYear) {
    lastMonthIndex = currentMonth;
  } else {
    return [];
  }

  return monthNames.slice(0, lastMonthIndex + 1).map((name, index) => ({
    label: name,
    value: String(index + 1),
  }));
}

export function formatToNumber(value) {
  const num = Number(value);

  if (isNaN(num)) return "00";

  if (num >= 10000000) {
    return (num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1) + "Cr";
  } else if (num >= 100000) {
    return (num / 100000).toFixed(num % 100000 === 0 ? 0 : 1) + "L";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + "K";
  }

  if (num < 10) {
    return `0${num}`;
  }

  return num.toString();
}
export function formatToIndianNumber(value) {
  const num = Number(value);

  if (isNaN(num)) return "0";

  return num.toLocaleString("en-IN");
}

export const vehicle_type_option = [
  { label: "Small Car", value: "Small Car" },
  { label: "Medium Car", value: "Medium Car" },
  { label: "Large Car", value: "Large Car" },
  { label: "Premium Car", value: "Premium Car" },
  { label: "Bike", value: "Bike" },
  { label: "Premium Bike", value: "Premium Bike" },
];

export const paymentMoethodOption = [
  {
    value: "UPI BUSINESS",
    label: "UPI Business",
  },
  {
    value: "UPI PERSONAL",
    label: "UPI Personal",
  },
  { value: "CASH", label: "Cash" },
  { value: "CARD", label: "Card" },
];

export const paymentStatusOption = [
  { label: "Paid", value: "PAID" },
  { label: "Unpaid", value: "UNPAID" },
  { label: "Partially paid", value: "PARTIALLY_PAID" },
];
export function paymentStatusBadge(status) {
  switch (status) {
    case "PAID":
      return (
        <span className="rounded-pill badge text-bg-success-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          <FaCheckCircle size={13} /> Paid
        </span>
      );

    case "UNPAID":
      return (
        <span className="rounded-pill badge text-bg-danger-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          <FaTimesCircle size={13} /> Unpaid
        </span>
      );

    case "PARTIALLY_PAID":
      return (
        <span className="rounded-pill badge text-bg-warning-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          <FaAdjust size={13} /> Partially Paid
        </span>
      );

    default:
      return (
        <span className="rounded-pill badge text-bg-secondary-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          {status}
        </span>
      );
  }
}

export function paymentMethodBadge(type) {
  switch (type) {
    case "UPI BUSINESS":
      return (
        <span className="badge text-bg-warning-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          <FaMobileAlt size={13} /> UPI BUSINESS
        </span>
      );

    case "UPI PERSONAL":
      return (
        <span className="badge text-bg-danger-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          <FaUser size={13} /> UPI PERSONAL
        </span>
      );

    case "CASH":
      return (
        <span className="badge text-bg-success-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          <FaMoneyBillWave size={13} /> Cash
        </span>
      );

    case "CARD":
      return (
        <span className="badge text-bg-primary-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          <FaCreditCard size={13} /> Card
        </span>
      );

    default:
      return (
        <span className="badge text-bg-secondary-soft d-inline-flex align-items-center gap-1 px-2 py-1">
          {type}
        </span>
      );
  }
}

export const CashFlowOption = [
  { label: "Advance", value: "Advance" },
  { label: "Petty Cash", value: "Petty Cash" },
  { label: "Cash Handover", value: "Cash Handover" },
  { label: "Bank Deposit", value: "Bank Deposit" },
];

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

export const generateMonthsFrom2025 = () => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const startYear = 2025;
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0 = Jan, 11 = Dec

  const months = [];

  // Loop from current year down to start year
  for (let year = currentYear; year >= startYear; year--) {
    const startM = year === startYear ? 0 : 0;
    const endM = year === currentYear ? currentMonth : 11;

    // Loop months in reverse
    for (let m = endM; m >= startM; m--) {
      const labelValue = `${monthNames[m]} ${year}`;
      months.push({
        label: labelValue,
        value: labelValue,
      });
    }
  }

  return months;
};

export const apartmentChoice = [
  { label: "Condominium", value: "Condominium" },
  { label: "Apartment", value: "Apartment" },
  { label: "Individual House", value: "Individual House" },
];

//  e.g. "September 2025"
//  e.g. "August 2025"

export function getPreviousMonth(monthYear) {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  if (!monthYear) return "";

  const [monthName, yearStr] = monthYear.split(" ");
  let monthIndex = months.indexOf(monthName);
  let year = parseInt(yearStr, 10);

  if (monthIndex === -1 || isNaN(year)) return "";

  // Previous month
  monthIndex -= 1;
  if (monthIndex < 0) {
    monthIndex = 11; // December
    year -= 1;
  }

  return `${months[monthIndex]} ${year}`;
}